import { useEffect, useMemo, useState } from 'react';
import './lineup-editor.styles.scss';
import { autoDepthChart, cleanDepthChart, DEPTH_LEVELS } from '@shared/depthChart.js';
import { overallAt } from '@shared/positions.js';
import { Link } from 'react-router-dom';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { lineupChanges } from '@/utils/lineupChanges';
import { positions } from '@/utils/positions';
import { levels } from '@/utils/levels';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import Modal from '@/components/modal/modal.component';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import TeamNav from '@/components/HOME/team-nav/team-nav.component';
import SortTh from '@/components/SHARED/sort-th/sort-th.component';
import { useSortable } from '@/utils/useSortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const LEVEL_NAMES = ['Starter', '2nd', '3rd', '4th'];

// The manager's depth chart: a starter and up to three backups at each
// position. Starters play most; backups come in, in order, when players
// tire, foul out or get hurt. Saved lineups are used for every game until
// changed.
const LineupEditor = ({ league, team, base }) => {

    const saved = team.depth ?? {};
    const savedKey = JSON.stringify(saved);
    const [ depth, setDepth ] = useState(saved);
    const [ saving, setSaving ] = useState(false);
    const [ message, setMessage ] = useState(null);
    const [ comparing, setComparing ] = useState(null);

    // Pick up a change saved elsewhere (another device) when not editing.
    useEffect(() => {
        setDepth(JSON.parse(savedKey));
    }, [savedKey])

    const players = team.players;
    const { errors } = cleanDepthChart(depth, players);
    const dirty = JSON.stringify(sortKeys(depth)) !== JSON.stringify(sortKeys(saved));

    // Options for each position: natural position first, then by overall.
    const optionsFor = useMemo(() => {
        const list = Object.values(players);
        return Object.fromEntries(positions.map(pos => {
            const fit = (p) => (p.pos1 === pos.num ? 0 : p.pos2 === pos.num ? 1 : 2);
            return [pos.id, [...list].sort((a, b) => fit(a) - fit(b) || b.overall - a.overall)];
        }));
    }, [players]);

    function setSlot(pos, level, playerId) {
        const key = `${pos.id}_${level}`;
        const next = { ...depth };
        const current = next[key];
        // Picking someone already listed at this position, or starting at
        // another one, swaps them with the player being replaced.
        for (const k of Object.keys(next)) {
            if (k === key || next[k] !== playerId) continue;
            if (k.startsWith(`${pos.id}_`) || (level === 0 && k.endsWith('_0'))) {
                if (current) next[k] = current;
                else delete next[k];
            }
        }
        if (playerId) next[key] = playerId;
        else delete next[key];
        setDepth(next);
        setMessage(null);
    }

    function autoFill() {
        // The computer coach's choice: by overall, skipping injured players.
        const neutral = Object.fromEntries(Object.values(players).map(p => [p.id, { ...p, chemistry: 5 }]));
        setDepth(autoDepthChart(neutral, 5, 0));
        setMessage(null);
    }

    async function save() {
        setSaving(true);
        const res = await UseCloudFunction('saveDepthChart', { leagueId: league.id, depth });
        setSaving(false);
        setMessage(res.error ? { error: true, text: res.error } : { text: 'Lineup saved. It will be used from the next game on.' });
    }

    const changes = lineupChanges(players, depth);

    return (
        <div className='lineup-editor'>
            <TeamNav base={base} />
            <div className='g-card lineup-intro'>
                <h2>Lineup <HelpLink section='lineup' label='How lineups work' /></h2>
                <p>
                    Choose a starter and up to three backups at each position. Starters play the most minutes.
                    When a player tires, fouls out or gets hurt, the next player listed comes in.
                    Injured players are skipped automatically.
                    How your team plays is set on the <Link to={`${base}/strategy`}>Strategy</Link> page.
                </p>
                <button className='g-button small-btn' onClick={autoFill}>Fill by overall rating</button>
            </div>

            {changes.length > 0 &&
                <div className='g-card lineup-warning' role='status'>
                    <b>Next game with this lineup:</b>
                    {changes.map(c => <div key={c.posId}>{c.text}</div>)}
                </div>
            }

            <div className='lineup-positions'>
                {positions.map(pos => {
                    const starter = players[depth[`${pos.id}_0`]];
                    const starterOut = isOut(starter);
                    return (
                        <section key={pos.id} className={`g-card lineup-position ${starterOut ? 'starter-out' : ''}`}>
                            <div className='lineup-position-head'>
                                <h3>{pos.short} <span className='pos-long'>{pos.long}</span></h3>
                                <button className='g-button small-btn' onClick={() => setComparing(pos)}>Compare</button>
                            </div>
                            {Array.from({ length: DEPTH_LEVELS }, (_, level) => {
                                const id = `lineup-${pos.id}-${level}`;
                                const value = depth[`${pos.id}_${level}`] ?? '';
                                const hurt = isOut(players[value]);
                                const listed = players[value];
                                return (
                                    <div key={level} className={`lineup-slot ${level === 0 ? 'starter' : ''} ${hurt ? 'injured' : ''}`}>
                                        <label htmlFor={id}>{LEVEL_NAMES[level]}</label>
                                        <select
                                            id={id}
                                            value={value}
                                            aria-invalid={hurt || undefined}
                                            aria-describedby={hurt ? `${id}-out` : undefined}
                                            onChange={(e) => setSlot(pos, level, e.target.value)}>
                                            <option value=''>{level === 0 ? 'Choose a starter' : 'None'}</option>
                                            {optionsFor[pos.id].map(p => (
                                                <option key={p.id} value={p.id}>{optionLabel(p, pos.num)}</option>
                                            ))}
                                        </select>
                                        {listed ? <SlotOverall player={listed} slot={pos.num} /> : <span />}
                                        {hurt &&
                                            <div className='slot-out' id={`${id}-out`}>
                                                <FontAwesomeIcon icon={faTriangleExclamation} /> Injured, out {gamesText(players[value])}.
                                                {level === 0 ? ' Choose another starter.' : ' The next player listed will play instead.'}
                                            </div>
                                        }
                                    </div>
                                )
                            })}
                            {starter && <PlayerLine player={starter} pos={pos} />}
                        </section>
                    )
                })}
            </div>

            <div className='lineup-actions'>
                <div className='lineup-status' role='status'>
                    {errors.length > 0 ? <span className='error'>{errors[0]}</span> :
                        message ? <span className={message.error ? 'error' : 'ok'}>{message.text}</span> :
                        dirty ? <span>You have unsaved changes.</span> :
                        <span className='meta'>{team.depthSavedAt ? 'Saved.' : 'This is the lineup your team came with.'}</span>
                    }
                </div>
                <div className='lineup-buttons'>
                    <button className='g-button' disabled={!dirty || saving} onClick={() => { setDepth(saved); setMessage(null); }}>Undo changes</button>
                    <button
                        className={`g-button primary ${(!dirty || errors.length || saving) ? 'disabled' : ''}`}
                        disabled={!dirty || errors.length > 0 || saving}
                        onClick={save}>
                        {saving ? <ThreeDotsLoader /> : 'Save lineup'}
                    </button>
                </div>
            </div>

            <Modal show={Boolean(comparing)} cancel={() => setComparing(null)} text={comparing ? `Compare ${comparing.long.toLowerCase()}s` : ''} closeButton={true} cls='wide'>
                {comparing &&
                    <CompareTable
                        pos={comparing}
                        players={players}
                        depth={depth}
                        onStart={(id) => { setSlot(comparing, 0, id); setComparing(null); }} />
                }
            </Modal>
        </div>
    )
}

// Columns for comparing players at one position. Visible ratings and
// season stats only.
const COMPARE_COLUMNS = [
    { key: 'overall', label: 'Ovr', get: (p) => p.overall },
    { key: 'off', label: 'Off', get: (p) => p.off_quality },
    { key: 'def', label: 'Def', get: (p) => p.def_quality },
    { key: 'sta', label: 'Sta', get: (p) => p.stamina },
    { key: '3pt', label: '3PT', get: (p) => p.off_3pt },
    { key: 'mid', label: 'Mid', get: (p) => p.off_shooting },
    { key: 'drv', label: 'Drive', get: (p) => p.off_drive },
    { key: 'pas', label: 'Pass', get: (p) => p.off_passing },
    { key: 'reb', label: 'Reb', get: (p) => Math.round((p.off_rebounds + p.def_rebounds) / 2) },
    { key: 'per', label: 'Perim D', get: (p) => p.def_speed },
    { key: 'int', label: 'Inside D', get: (p) => p.def_paint },
    { key: 'stl', label: 'Stl', get: (p) => p.def_steals },
    { key: 'blk', label: 'Blk', get: (p) => p.def_blocks },
    { key: 'mpg', label: 'MPG', get: (p) => perGame(p, 'min') },
    { key: 'ppg', label: 'PPG', get: (p) => perGame(p, 'pts') },
];

// Everyone who plays this position or is listed at it, with the best
// value in each column marked.
const CompareTable = ({ pos, players, depth, onStart }) => {
    const sorter = useSortable();
    const listedLevel = (id) => [0, 1, 2, 3].find(n => depth[`${pos.id}_${n}`] === id);
    const rows = sorter.apply(Object.values(players)
        .filter(p => p.pos1 === pos.num || p.pos2 === pos.num || listedLevel(p.id) !== undefined)
        .sort((a, b) => (listedLevel(a.id) ?? 9) - (listedLevel(b.id) ?? 9) || b.overall - a.overall), {
        name: p => p.lName, role: p => listedLevel(p.id) ?? null,
        ...Object.fromEntries(COMPARE_COLUMNS.map(c => [c.key, c.get])),
    });
    const best = Object.fromEntries(COMPARE_COLUMNS.map(c => [c.key,
        Math.max(...rows.map(p => c.get(p) ?? -1))]));

    return (
        <div className='modal-children compare-players'>
            <p className='meta'>Everyone who plays {pos.short} or is listed there, in depth chart order. Tap a column to sort by it. Highlighted numbers are the best in each column.</p>
            <div className='table-scroll'>
                <table className='g-table compare-table'>
                    <thead>
                        <tr>
                            <SortTh k='name' label='Player' sorter={sorter} first='asc' />
                            <SortTh k='role' label='Role' sorter={sorter} first='asc' />
                            {COMPARE_COLUMNS.map(c => <SortTh key={c.key} k={c.key} label={c.label} sorter={sorter} className='num' />)}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(p => {
                            const level = listedLevel(p.id);
                            const hurt = p.injury?.gamesOut > 0;
                            return (
                                <tr key={p.id} className={hurt ? 'injured' : ''}>
                                    <td>
                                        <b>{p.fName} {p.lName}</b>
                                        {hurt && <span className='out-tag'><FontAwesomeIcon icon={faTriangleExclamation} /> Out {p.injury.gamesOut}</span>}
                                        <div className='meta'>{posLabel(p)} · {classLabel(p)}</div>
                                    </td>
                                    <td>{level === undefined ? '-' : LEVEL_NAMES[level]}</td>
                                    {COMPARE_COLUMNS.map(c => {
                                        const v = c.get(p);
                                        return <td key={c.key} className={`num ${v !== null && v === best[c.key] ? 'best' : ''}`}>{v ?? '-'}</td>;
                                    })}
                                    <td>
                                        {level !== 0 &&
                                            <button className='g-button small-btn' onClick={() => onStart(p.id)}>Start</button>
                                        }
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function perGame(p, key) {
    return p.season?.gp ? Number((p.season[key] / p.season.gp).toFixed(1)) : null;
}

// The player's overall at the position they'll play: a point lower at
// their second position, and lower still, in red, out of position.
const SlotOverall = ({ player: p, slot }) => {
    const here = overallAt(p, slot);
    const off = offPosition(p, slot);
    return (
        <span
            className={`slot-ovr ${off ? 'lower' : ''}`}
            title={here < p.overall ? `${here} at ${positions[slot - 1].short} (${p.overall} at ${posLabel(p)})` : `Overall ${here}`}>
            {here}{off && <span className='sr-only'> at {positions[slot - 1].short}, {p.overall} at {posLabel(p)}</span>}
        </span>
    )
}

const PlayerLine =({ player: p, pos }) => {
    const natural = !offPosition(p, pos.num);
    const here = overallAt(p, pos.num);
    return (
        <div className='player-line meta'>
            Starter: Ovr {natural ? here : <><span className='lower'>{here}</span> ({p.overall} at {posLabel(p)})</>}
            {' '}· Off {p.off_quality} · Def {p.def_quality} · Sta {p.stamina} · {classLabel(p)}
            {!natural && <span className='off-pos'> · Usually plays {posLabel(p)}</span>}
        </div>
    )
}

function isOut(p) {
    return p?.injury?.gamesOut > 0;
}

function gamesText(p) {
    const n = p.injury.gamesOut;
    return `${n} more game${n === 1 ? '' : 's'}`;
}

function offPosition(p, slot) {
    return slot !== p.pos1 && slot !== p.pos2;
}

function optionLabel(p, slot) {
    const here = overallAt(p, slot);
    const ovr = offPosition(p, slot) ? `${here} here (${p.overall})` : here;
    return `${p.fName} ${p.lName} · ${posLabel(p)} · ${ovr}${p.injury?.gamesOut > 0 ? ' · OUT' : ''}`;
}

function posLabel(p) {
    return positions[p.pos1 - 1].short + (p.pos2 ? `/${positions[p.pos2 - 1].short}` : '');
}

function classLabel(p) {
    return p.year ? levels[p.year - 1].short : `Age ${p.age}`;
}

function sortKeys(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v).sort(([a], [b]) => a.localeCompare(b)));
}

export default LineupEditor
